import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Users, ShieldCheck, PenLine, History, Plus, Pencil, Trash2, Search,
  Copy, Power, Check, X, Save, AlertTriangle,
} from 'lucide-react';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { useWarehouse } from '../../Components/useWarehouse/useWarehouse';
import { api, setCurrentPageContext } from '../../services/api';
import { Modal } from '../../Components/Modal/Modal';
import { Tabs } from '../../Components/Common/Tabs';
import { AccordionSection } from '../../Components/Common/Accordion';
import { ConfirmDialog } from '../../Components/Common/ConfirmDialog';
import { useUnsavedChangesGuard } from '../../Components/Common/useUnsavedChangesGuard';
import { SignatureCell } from './AdministracionTIPanel';
import TotpApprovals from '../TotpApprovals';

// ---------------------------------------------------------------------
// Catálogos: mismos 11 permisos especiales y mismos 8 módulos/secciones
// que ya existían (dispersos en 2-3 copias dentro de AdministracionTIPanel.jsx).
// Se centralizan aquí en un solo lugar para el rediseño.
// ---------------------------------------------------------------------

const ROLE_OPTIONS = [
  'Administrador', 'Administrativo', 'Operativo', 'Operativo-Inspector',
  'Almacen', 'Recursos Humanos', 'Contabilidad', 'Gestión de compras', 'Transportes',
];

const FLAG_LABELS = {
  purchaseCanApproveRequests: 'Aprueba requisiciones',
  purchaseCanApproveOrders: 'Aprueba OC',
  purchaseCanManageRequests: 'Gestiona requisiciones',
  purchaseCanEditOrders: 'Edita OC',
  purchaseCanManagePaymentStatus: 'Marca OC como facturada/pagada',
  overtimeFinalApprover: 'Ve/aprueba novedades y horas extra de toda la empresa',
  preopAuthSst: 'Firma preop. como Coordinador SST',
  preopAuthSeguridad: 'Firma preop. como Seguridad Física',
  preopAuthTransportes: 'Firma preop. como Coord. Área Transportes',
  overtimeCanEditApproved: 'Corrige horas extra ya Aprobadas',
  hrNoveltiesCanExport: 'Exporta novedades de RRHH a Excel',
  expenseCanExport: 'Exporta solicitud de gastos a Excel',
  expenseCanManage: 'Ve y valida/aprueba/rechaza solicitudes de gastos de toda la empresa',
  noveltyHrCanReceive: 'Recibe (firma) o rechaza novedades de RRHH ya aprobadas por el jefe inmediato',
  preopCanApprove: 'Aprueba preoperacionales (después de Coordinador y SST, antes de Seguridad Física)',
};

const FLAG_TO_COLUMN = {
  purchaseCanApproveRequests: 'purchase_can_approve_requests',
  purchaseCanApproveOrders: 'purchase_can_approve_orders',
  purchaseCanManageRequests: 'purchase_can_manage_requests',
  purchaseCanEditOrders: 'purchase_can_edit_orders',
  purchaseCanManagePaymentStatus: 'purchase_can_manage_payment_status',
  overtimeFinalApprover: 'overtime_final_approver',
  preopAuthSst: 'preop_auth_sst',
  preopAuthSeguridad: 'preop_auth_seguridad',
  preopAuthTransportes: 'preop_auth_transportes',
  overtimeCanEditApproved: 'overtime_can_edit_approved',
  hrNoveltiesCanExport: 'hr_novelties_can_export',
  expenseCanExport: 'expense_can_export',
  expenseCanManage: 'expense_can_manage',
  noveltyHrCanReceive: 'novelty_hr_can_receive',
  preopCanApprove: 'preop_can_approve',
};

const FLAG_PERM_KEY = (flag) => `flag.${FLAG_TO_COLUMN[flag]}`;

// Agrupación en "permisos especiales" con advertencia para el de mayor
// impacto — ver Fase 2 del plan (categorías acordadas).
const PERMISSION_CATEGORIES = [
  { id: 'compras', label: 'Compras', flags: ['purchaseCanApproveRequests', 'purchaseCanApproveOrders', 'purchaseCanManageRequests', 'purchaseCanEditOrders', 'purchaseCanManagePaymentStatus'] },
  { id: 'firmas', label: 'Firmas', flags: ['preopAuthSst', 'preopAuthSeguridad', 'preopAuthTransportes'] },
  { id: 'aprobaciones', label: 'Aprobaciones', flags: ['overtimeFinalApprover', 'expenseCanManage', 'noveltyHrCanReceive', 'preopCanApprove'] },
  { id: 'correcciones', label: 'Correcciones', flags: ['overtimeCanEditApproved'], warning: 'Este permiso permite modificar información previamente aprobada.' },
  { id: 'exportaciones', label: 'Exportaciones', flags: ['hrNoveltiesCanExport', 'expenseCanExport'] },
];

const MODULE_LIST = [
  { key: 'recursos-humanos', label: 'Recursos Humanos' },
  { key: 'almacen', label: 'Almacén' },
  { key: 'sgi', label: 'SIG' },
  { key: 'contabilidad', label: 'Contabilidad' },
  { key: 'transportes', label: 'Transportes' },
  { key: 'seguridad', label: 'Seguridad' },
  { key: 'control-operativo', label: 'Control Operativo' },
  { key: 'administracion-ti', label: 'Administración TI' },
];

const CONTABILIDAD_SECTION_KEYS = [
  'solicitudes', 'novedades', 'solicitud-gastos', 'proveedores', 'centros-costo', 'gestion-compras',
  'novedades-gestion', 'novedades-colaborador', 'novedades-reporte',
  'novedades-estado-todos', 'novedades-estado-pendientes', 'novedades-estado-validadas', 'novedades-estado-aprobadas', 'novedades-estado-rechazadas',
];
const RECURSOS_HUMANOS_SECTION_KEYS = ['indice', 'activos', 'cv', 'vacaciones', 'novedades', 'indicadores', 'dotaciones'];

const SECTION_LABEL_MAP = {
  almacen: {
    'marca-item': 'Marca', materiales: 'Materiales', bodegas: 'Bodegas', pedidos: 'Pedidos',
    entrada: 'Entrada', asignacion: 'Asignación', prestamo: 'Préstamo', devoluciones: 'Devoluciones',
    salidas: 'Salidas', traslados: 'Traslados', consumibles: 'Consumibles', stock: 'Existencias',
    'consulta-responsable': 'Consulta por responsable', 'consulta-serial': 'Consulta por serial',
    historial: 'Historial', alertas: 'Alertas', indicadores: 'Indicadores',
  },
  contabilidad: {
    'libro-diario': 'Libro diario', cuentas: 'Cuentas', facturacion: 'Facturación', reportes: 'Reportes',
    solicitudes: 'Solicitudes', proveedores: 'Proveedores', 'centros-costo': 'Centros de costo',
    novedades: 'Novedades', 'solicitud-gastos': 'Solicitud de gastos',
    'gestion-compras': 'Gestión de compras (ver compras de toda la empresa)',
    'novedades-gestion': 'Novedades: pestaña Gestión',
    'novedades-colaborador': 'Novedades: pestaña Por colaborador',
    'novedades-reporte': 'Novedades: pestaña Reporte',
    'novedades-estado-todos': 'Novedades (Gestión): filtro Todos',
    'novedades-estado-pendientes': 'Novedades (Gestión): filtro Pendientes',
    'novedades-estado-validadas': 'Novedades (Gestión): filtro Validadas',
    'novedades-estado-aprobadas': 'Novedades (Gestión): filtro Aprobadas',
    'novedades-estado-rechazadas': 'Novedades (Gestión): filtro Rechazadas',
  },
  'recursos-humanos': {
    indice: 'Nuevo colaborador', activos: 'Colaboradores activos', cv: 'Hoja de vida',
    vacaciones: 'Control vacaciones', novedades: 'Novedades',
    indicadores: 'Indicadores del área', dotaciones: 'Control de dotaciones',
  },
  transportes: {
    flota: 'Flota', preoperacionales: 'Preoperacionales', conductores: 'Conductores',
    mantenimientos: 'Mantenimientos', control_kilometraje: 'Control kilometraje',
    control_tanqueos: 'Control tanqueos', asignacion_vehiculos: 'Asignación de vehículos',
    checklist_por_vehiculo: 'Checklist por vehículo', pesv: 'PESV', indicadores: 'Indicadores',
  },
};

const prettySection = (moduleKey, sectionKey) => {
  const byMod = SECTION_LABEL_MAP[moduleKey];
  if (byMod && byMod[sectionKey]) return byMod[sectionKey];
  return String(sectionKey || '').replace(/[-_]+/g, ' ');
};

const onlyDigits = (v) => String(v || '').replace(/\D+/g, '');

// Firma digital ya cargada por colaborador (people.firma_digital, vía
// useWarehouse), indexada por cédula sin puntos/guiones — mismo mapeo que
// ya existía en la versión anterior de esta pantalla. Sin esto, el tab de
// Firmas y la pestaña "Firma" del formulario de usuario siempre se veían
// vacíos aunque la persona ya tuviera una firma en archivo.
function useSignatureByCedula() {
  const { people } = useWarehouse();
  return useMemo(() => {
    const map = {};
    (Array.isArray(people) ? people : []).forEach((p) => {
      const digits = onlyDigits(p.dni);
      if (digits && p.signature) map[digits] = p.signature;
    });
    return map;
  }, [people]);
}

const emptyForm = () => ({
  name: '', email: '', role: 'Consulta', password: '', warehouseId: 'popayan',
  responsibleCedula: '', areaName: '',
  purchaseCanApproveRequests: false, purchaseCanApproveOrders: false,
  purchaseCanManageRequests: false, purchaseCanEditOrders: false,
  purchaseCanManagePaymentStatus: false,
  overtimeFinalApprover: false, preopAuthSst: false, preopAuthSeguridad: false,
  preopAuthTransportes: false, overtimeCanEditApproved: false,
  hrNoveltiesCanExport: false, expenseCanExport: false, expenseCanManage: false,
  noveltyHrCanReceive: false,
  preopCanApprove: false,
  overtimeAllowedProjects: '',
});

const formFromUser = (u) => ({
  name: u.name || '', email: u.email || '', role: u.role || 'Consulta', password: u.password || '',
  warehouseId: u.warehouseId || 'popayan',
  responsibleCedula: u.responsibleCedula || u.responsible_cedula || '',
  areaName: u.areaName || u.area_name || '',
  purchaseCanApproveRequests: !!(u.purchaseCanApproveRequests ?? u.purchase_can_approve_requests ?? false),
  purchaseCanApproveOrders: !!(u.purchaseCanApproveOrders ?? u.purchase_can_approve_orders ?? false),
  purchaseCanManageRequests: !!(u.purchaseCanManageRequests ?? u.purchase_can_manage_requests ?? false),
  purchaseCanEditOrders: !!(u.purchaseCanEditOrders ?? u.purchase_can_edit_orders ?? false),
  purchaseCanManagePaymentStatus: !!(u.purchaseCanManagePaymentStatus ?? u.purchase_can_manage_payment_status ?? false),
  overtimeFinalApprover: !!(u.overtimeFinalApprover ?? u.overtime_final_approver ?? false),
  preopAuthSst: !!(u.preopAuthSst ?? u.preop_auth_sst ?? false),
  preopAuthSeguridad: !!(u.preopAuthSeguridad ?? u.preop_auth_seguridad ?? false),
  preopAuthTransportes: !!(u.preopAuthTransportes ?? u.preop_auth_transportes ?? false),
  overtimeCanEditApproved: !!(u.overtimeCanEditApproved ?? u.overtime_can_edit_approved ?? false),
  hrNoveltiesCanExport: !!(u.hrNoveltiesCanExport ?? u.hr_novelties_can_export ?? false),
  expenseCanExport: !!(u.expenseCanExport ?? u.expense_can_export ?? false),
  expenseCanManage: !!(u.expenseCanManage ?? u.expense_can_manage ?? false),
  noveltyHrCanReceive: !!(u.noveltyHrCanReceive ?? u.novelty_hr_can_receive ?? false),
  preopCanApprove: !!Number(u.preopCanApprove ?? u.preop_can_approve ?? 0),
  overtimeAllowedProjects: u.overtimeAllowedProjects ?? u.overtime_allowed_projects ?? '',
});

// ---------------------------------------------------------------------
// Pestaña "Rol y permisos" del formulario de usuario
// ---------------------------------------------------------------------
function RolePermissionsTab({ form, setForm, isEditing, userId, roles, rolesLoading, onApplyTemplate, applyingTemplate }) {
  const [openCategory, setOpenCategory] = useState(null);
  const [modulesOpen, setModulesOpen] = useState(false);

  const selectedRole = useMemo(() => roles.find((r) => r.display_name === form.role) || null, [roles, form.role]);
  const roleDefaultKeys = useMemo(() => new Set(selectedRole?.default_permission_keys || []), [selectedRole]);

  const toggleFlag = (flag) => setForm((f) => ({ ...f, [flag]: !f[flag] }));

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium mb-1">Rol</label>
        <div className="flex items-center gap-2">
          <select
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm bg-white flex-1"
          >
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button
            type="button"
            disabled={!isEditing || !selectedRole || rolesLoading || applyingTemplate}
            onClick={() => onApplyTemplate(selectedRole?.id)}
            title={!isEditing ? 'Guarda el usuario primero para poder aplicar la plantilla del rol' : undefined}
            className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
          >
            <Copy size={14} /> {applyingTemplate ? 'Aplicando...' : 'Aplicar valores por defecto del rol'}
          </button>
        </div>
        {!isEditing && (
          <div className="text-xs text-gray-500 mt-1">Guarda el usuario primero para poder aplicar la plantilla de permisos del rol.</div>
        )}
      </div>

      <div>
        <div className="text-sm font-semibold text-gray-800 mb-2">Permisos especiales</div>
        <div className="space-y-2">
          {PERMISSION_CATEGORIES.map((cat) => {
            const activeCount = cat.flags.filter((f) => form[f]).length;
            return (
              <AccordionSection
                key={cat.id}
                id={cat.id}
                title={cat.label}
                subtitle={cat.warning ? undefined : `${activeCount} de ${cat.flags.length} activos`}
                badge={cat.flags.length}
                accentColor={cat.warning ? '#DC2626' : '#2563EB'}
                open={openCategory === cat.id}
                onToggle={(id) => setOpenCategory((v) => (v === id ? null : id))}
              >
                {cat.warning && (
                  <div className="mb-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{cat.warning}</span>
                  </div>
                )}
                <div className="space-y-2">
                  {cat.flags.map((flag) => {
                    const checked = !!form[flag];
                    const permKey = FLAG_PERM_KEY(flag);
                    const fromRole = checked && roleDefaultKeys.has(permKey);
                    return (
                      <label key={flag} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2 bg-gray-50">
                        <span className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={checked} onChange={() => toggleFlag(flag)} />
                          {FLAG_LABELS[flag]}
                        </span>
                        {checked && (
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border shrink-0 ${fromRole ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                            {fromRole ? 'Heredado del rol' : 'Individual'}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </AccordionSection>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-sm font-semibold text-gray-800 mb-1">Horas extra: proyectos permitidos</div>
        <p className="text-xs text-gray-500 mb-2">
          Restringe a este jefe/coordinador a ver y gestionar solo las horas extra cuyo campo "Proyecto"
          esté en esta lista (ej. <code>EMCALI,EMCALI-ALUMBRADO-PUBLICO</code>) — tiene prioridad sobre
          cualquier otro permiso de horas extra, incluida la aprobación final. Déjalo vacío para no
          restringir (comportamiento normal: ve lo de su propio equipo, o de toda la empresa si tiene
          aprobación final).
        </p>
        <input
          type="text"
          value={form.overtimeAllowedProjects || ''}
          onChange={(e) => setForm((f) => ({ ...f, overtimeAllowedProjects: e.target.value }))}
          placeholder="Ej: EMCALI,EMCALI-ALUMBRADO-PUBLICO"
          className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
        />
      </div>

      <ModuleSectionsAccordion open={modulesOpen} setOpen={setModulesOpen} userId={userId} isEditing={isEditing} />
    </div>
  );
}

// Acordeón de módulos/secciones (visibilidad, admin_prefs) — sigue siendo
// simple on/off, sin heredado/individual todavía (Fase 1 dejó esto fuera
// del modelo nuevo a propósito).
function ModuleSectionsAccordion({ userId, isEditing }) {
  const { getModulePrefs, getModulePrefsRaw, setModulePrefs, getSectionPrefs, getSectionPrefsRaw, setSectionPrefs, getDefaultPrefs, currentUser } = useUserPrefs();
  const canManage = String(currentUser?.email || '').trim().toLowerCase() === 'admin@senerfi.com';
  const [openModule, setOpenModule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const mp = (canManage ? getModulePrefsRaw?.(userId) : getModulePrefs(userId)) || {};
  const sp = (canManage ? getSectionPrefsRaw?.(userId) : getSectionPrefs(userId)) || {};
  const allowedAll = getDefaultPrefs?.().section || {};

  const toggleModule = (key) => {
    if (!isEditing) return;
    setModulePrefs(userId, { ...mp, [key]: !mp[key] });
  };
  const toggleSection = (moduleKey, sectionKey) => {
    if (!isEditing) return;
    const mod = sp[moduleKey] || {};
    setSectionPrefs(userId, moduleKey, { ...mod, [sectionKey]: !mod[sectionKey] });
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.admin.prefs.upsert({ user_id: userId, module_prefs: JSON.stringify(mp), section_prefs: JSON.stringify(sp) });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (_) {
      // Igual que antes: si falla la llamada, el estado ya quedó
      // actualizado localmente vía setModulePrefs/setSectionPrefs.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-gray-800">Módulos y secciones</div>
        {isEditing && (
          <button type="button" onClick={savePrefs} disabled={saving} className="text-xs px-2.5 py-1 rounded-lg border bg-white hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50">
            <Save size={12} /> {saving ? 'Guardando...' : savedFlash ? 'Guardado ✓' : 'Guardar módulos/secciones'}
          </button>
        )}
      </div>
      <div className="space-y-2">
        {MODULE_LIST.map((m) => {
          const allowed = allowedAll[m.key] || {};
          const extraKeys = m.key === 'contabilidad'
            ? CONTABILIDAD_SECTION_KEYS
            : (m.key === 'recursos-humanos' ? RECURSOS_HUMANOS_SECTION_KEYS : []);
          const sectionKeys = Array.from(new Set([...Object.keys(allowed || {}), ...extraKeys])).sort();
          const moduleOn = !!mp[m.key];
          return (
            <AccordionSection
              key={m.key}
              id={m.key}
              title={m.label}
              subtitle={moduleOn ? 'Visible' : 'Oculto'}
              accentColor={moduleOn ? '#059669' : '#9CA3AF'}
              open={openModule === m.key}
              onToggle={(id) => setOpenModule((v) => (v === id ? null : id))}
            >
              <label className="flex items-center gap-2 text-sm font-medium mb-3">
                <input type="checkbox" checked={moduleOn} onChange={() => toggleModule(m.key)} disabled={!isEditing} />
                Ver módulo {m.label}
              </label>
              {sectionKeys.length === 0 ? (
                <div className="text-xs text-gray-400">Este módulo no tiene secciones configurables.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-2">
                  {sectionKeys.map((sk) => (
                    <label key={sk} className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!(sp[m.key] || {})[sk]}
                        onChange={() => toggleSection(m.key, sk)}
                        disabled={!isEditing}
                      />
                      {prettySection(m.key, sk)}
                    </label>
                  ))}
                </div>
              )}
            </AccordionSection>
          );
        })}
      </div>
    </div>
  );
}

// Pestaña "Seguridad" (2FA) del formulario de un usuario puntual.
function UserSecurityTab({ user }) {
  const [status, setStatus] = useState(null); // { totp_enabled, totp_awaiting_approval, id }
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!user?.email) { setLoading(false); return; }
    setLoading(true);
    setError('');
    setNotFound(false);
    try {
      const res = await api.auth.totpAdmin.search(user.email);
      const rows = Array.isArray(res?.data) ? res.data : [];
      const found = rows.find((r) => Number(r.id) === Number(user.id));
      if (found) {
        setStatus(found);
      } else {
        // El correo del formulario no coincide con el guardado en el
        // usuario (ej. se editó sin guardar) — sin esto, el estado
        // quedaba indistinguible de "Inactivo" y el botón de reset
        // desaparecía sin ninguna explicación.
        setStatus(null);
        setNotFound(true);
      }
    } catch (e) {
      setError(e?.message || 'No fue posible consultar el estado de 2FA');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    setBusy(true);
    setError('');
    try {
      if (action === 'approve') await api.auth.totpAdmin.approve(user.id);
      if (action === 'reject') await api.auth.totpAdmin.reject(user.id);
      if (action === 'reset') await api.auth.totpAdmin.reset(user.id);
      await load();
    } catch (e) {
      setError(e?.message || 'No fue posible completar la acción');
    } finally {
      setBusy(false);
    }
  };

  if (!user?.id) {
    return <div className="text-sm text-gray-500">Guarda el usuario primero para gestionar su verificación en dos pasos.</div>;
  }
  if (loading) return <div className="text-sm text-gray-500">Cargando...</div>;

  if (notFound) {
    return (
      <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        No se pudo ubicar el estado de 2FA de este usuario con el correo actual del formulario
        ({user?.email || 'sin correo'}). Si acabas de cambiar el correo, guarda los cambios primero
        y vuelve a abrir esta pestaña.
      </div>
    );
  }

  const badge = status?.totp_enabled
    ? { label: 'Activo', cls: 'bg-green-50 text-green-700 border-green-200' }
    : status?.totp_awaiting_approval
      ? { label: 'Pendiente de aprobación', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
      : { label: 'Inactivo', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  const canReset = !!status?.totp_enabled || !!status?.totp_awaiting_approval;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className={`text-xs px-2.5 py-1 rounded-full border ${badge.cls}`}>{badge.label}</span>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
      <div className="flex flex-wrap gap-2">
        {status?.totp_awaiting_approval && (
          <>
            <button type="button" disabled={busy} onClick={() => act('approve')} className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm inline-flex items-center gap-1 disabled:opacity-50">
              <Check size={14} /> Aprobar
            </button>
            <button type="button" disabled={busy} onClick={() => act('reject')} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm inline-flex items-center gap-1 disabled:opacity-50">
              <X size={14} /> Rechazar
            </button>
          </>
        )}
        <button
          type="button"
          disabled={busy || !canReset}
          onClick={() => act('reset')}
          className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm disabled:opacity-50"
          title={canReset ? '' : 'Este usuario no tiene 2FA activo ni pendiente'}
        >
          Resetear 2FA
        </button>
      </div>
    </div>
  );
}

// Pestaña "Auditoría" del formulario de un usuario puntual — reutiliza el
// mismo endpoint admin_audit.php que la pantalla general de Auditoría,
// pre-filtrado por user_id.
function UserAuditTab({ userId, moduleKey, requireUser = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (requireUser && !userId) { setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const filters = { limit: 100, order: 'desc' };
        if (userId) filters.user_id = userId;
        if (moduleKey) filters.module_key = moduleKey;
        const res = await api.admin.audit.list(filters);
        if (alive) setRows(Array.isArray(res?.data) ? res.data : []);
      } catch (_) {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, moduleKey, requireUser]);

  if (requireUser && !userId) return <div className="text-sm text-gray-500">Guarda el usuario primero para ver su historial de auditoría.</div>;
  if (loading) return <div className="text-sm text-gray-500">Cargando...</div>;
  if (!rows.length) return <div className="text-sm text-gray-500">Sin eventos de auditoría para este usuario.</div>;

  return (
    <div className="space-y-2 max-h-96 overflow-auto">
      {rows.map((r) => (
        <div key={r.id} className="border rounded-lg px-3 py-2 text-sm">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{r.created_at}</span>
            <span>{r.module_key || 'general'}</span>
          </div>
          <div className="text-gray-800">{r.action_name || r.event_type}{r.endpoint_name ? ` · ${r.endpoint_name}` : ''}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Modal de crear/editar usuario, con sub-pestañas.
// ---------------------------------------------------------------------
function UserFormModal({ user, onClose, roles, rolesLoading, reloadRoles, flash }) {
  const { addUser, updateUser } = useUserPrefs();
  const signatureByCedula = useSignatureByCedula();
  const isEditing = !!user?.id;
  const [tab, setTab] = useState('informacion');
  const [form, setForm] = useState(() => (user ? formFromUser(user) : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [userId, setUserId] = useState(user?.id || null);
  const [signatureOverride, setSignatureOverride] = useState('');

  const guard = useUnsavedChangesGuard(form);

  const requestClose = () => guard.requestClose(onClose);

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setError('Nombre y correo son obligatorios');
      return;
    }
    if (!String(form.responsibleCedula || '').trim()) {
      setError('La cédula es obligatoria: se usa para iniciar sesión.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isEditing) {
        await updateUser(userId, form);
        guard.markSaved(form);
        flash('success', 'Cambios guardados');
      } else {
        const res = await addUser(form);
        const newId = res?.id || null;
        if (newId) setUserId(newId);
        guard.markSaved(form);
        flash('success', 'Usuario creado');
      }
    } catch (e) {
      setError(e?.message || 'No fue posible guardar el usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async (roleId) => {
    if (!roleId || !userId) return;
    setApplyingTemplate(true);
    setError('');
    try {
      const res = await api.admin.roles.applyTemplate({ user_id: userId, role_id: roleId });
      const applied = res?.applied || [];
      // Reflejar en el formulario los flags que la plantilla acaba de otorgar.
      setForm((f) => {
        const next = { ...f };
        applied.forEach((permKey) => {
          const col = permKey.replace('flag.', '');
          const flag = Object.keys(FLAG_TO_COLUMN).find((k) => FLAG_TO_COLUMN[k] === col);
          if (flag) next[flag] = true;
        });
        return next;
      });
      flash('success', applied.length ? `Plantilla aplicada: ${applied.length} permiso(s)` : 'El rol no tiene permisos por defecto configurados');
    } catch (e) {
      setError(e?.message || 'No fue posible aplicar la plantilla del rol');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const tabs = [
    { id: 'informacion', label: 'Información' },
    { id: 'rol', label: 'Rol y permisos' },
    { id: 'firma', label: 'Firma' },
    { id: 'seguridad', label: 'Seguridad' },
    { id: 'auditoria', label: 'Auditoría' },
  ];

  return (
    <Modal isOpen onClose={requestClose} title={isEditing ? `Editar usuario — ${user.name || ''}` : 'Crear usuario'} size="max-w-4xl">
      <Tabs tabs={tabs} value={tab} onChange={setTab} className="mb-4" />

      {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {tab === 'informacion' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Nombre</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Correo</label>
            <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Contraseña {isEditing ? '(dejar vacío para no cambiar)' : ''}</label>
            <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cédula (responsable)</label>
            <input value={form.responsibleCedula} onChange={(e) => setForm((f) => ({ ...f, responsibleCedula: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bodega / sede</label>
            <select value={form.warehouseId} onChange={(e) => setForm((f) => ({ ...f, warehouseId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
              <option value="popayan">Popayán</option>
              <option value="santander-de-quilichao">Santander de Quilichao</option>
              <option value="emcali">Emcali</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Área (compras/dependencia)</label>
            <input value={form.areaName} onChange={(e) => setForm((f) => ({ ...f, areaName: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
      )}

      {tab === 'rol' && (
        <RolePermissionsTab
          form={form}
          setForm={setForm}
          isEditing={isEditing || !!userId}
          userId={userId}
          roles={roles}
          rolesLoading={rolesLoading}
          onApplyTemplate={handleApplyTemplate}
          applyingTemplate={applyingTemplate}
        />
      )}

      {tab === 'firma' && (
        <div>
          {!form.responsibleCedula ? (
            <div className="text-sm text-gray-500">Ingresa primero la cédula del usuario en la pestaña Información.</div>
          ) : (
            <SignatureCell
              cedula={form.responsibleCedula}
              signatureUrl={signatureOverride || signatureByCedula[onlyDigits(form.responsibleCedula)] || ''}
              onUploaded={(_digits, url) => { setSignatureOverride(url); flash('success', 'Firma cargada'); }}
            />
          )}
          <div className="text-xs text-gray-400 mt-2">Si no hay registro de este colaborador en Recursos Humanos, la carga de firma no estará disponible.</div>
        </div>
      )}

      {tab === 'seguridad' && <UserSecurityTab user={userId ? { id: userId, email: form.email } : null} />}

      {tab === 'auditoria' && <UserAuditTab userId={userId} />}

      <div className="mt-6 flex justify-end gap-2 border-t pt-4">
        <button type="button" onClick={requestClose} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm">Cerrar</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-lg text-white text-sm shadow-sm hover:opacity-95 disabled:opacity-60"
          style={{ background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' }}
        >
          <Save size={14} className="inline mr-1.5 -mt-0.5" /> {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>

      <ConfirmDialog
        open={!!guard.confirmState}
        title="Cambios sin guardar"
        message="Hay cambios sin guardar en este formulario. Si continúas, se perderán."
        confirmLabel="Continuar sin guardar"
        cancelLabel="Cancelar"
        onConfirm={guard.confirmDiscard}
        onCancel={guard.cancelDiscard}
        danger
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Pestaña "Usuarios": tabla + búsqueda/filtros + modal de crear/editar
// ---------------------------------------------------------------------
function UsersTab({ roles, rolesLoading, flash }) {
  const { users, deleteUser, setUserActive } = useUserPrefs();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (roleFilter !== 'Todos' && u.role !== roleFilter) return false;
      if (statusFilter === 'Activos' && !u.active) return false;
      if (statusFilter === 'Inactivos' && u.active) return false;
      if (!q) return true;
      return String(u.name || '').toLowerCase().includes(q)
        || String(u.email || '').toLowerCase().includes(q)
        || String(u.role || '').toLowerCase().includes(q);
    });
  }, [users, search, roleFilter, statusFilter]);

  const openCreate = () => { setEditingUser(null); setModalOpen(true); };
  const openEdit = (u) => { setEditingUser(u); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingUser(null); };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try { await deleteUser(u.id); flash('success', 'Usuario eliminado'); }
    catch (e) { flash('error', e?.message || 'No fue posible eliminar'); }
  };
  const handleToggleActive = async (u) => {
    try { await setUserActive(u.id, !u.active); flash('success', u.active ? 'Usuario desactivado' : 'Usuario activado'); }
    catch (e) { flash('error', e?.message || 'No fue posible actualizar'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, correo o rol" className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm bg-white" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white">
          <option value="Todos">Todos los roles</option>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white">
          <option value="Todos">Todos los estados</option>
          <option value="Activos">Activos</option>
          <option value="Inactivos">Inactivos</option>
        </select>
        <button type="button" onClick={openCreate} className="px-3 py-2 rounded-lg text-white text-sm shadow-sm hover:opacity-95 inline-flex items-center gap-2" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}>
          <Plus size={16} /> Crear usuario
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Usuario</th>
                <th className="px-4 py-3 font-medium">Correo</th>
                <th className="px-4 py-3 font-medium">Rol</th>
                <th className="px-4 py-3 font-medium">Sede</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Sin resultados para los filtros seleccionados.</td></tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3">{u.role}</td>
                  <td className="px-4 py-3">{u.warehouseId || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${u.active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {u.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" onClick={() => openEdit(u)} className="px-2.5 py-1 rounded-lg border bg-white hover:bg-gray-50 text-xs inline-flex items-center gap-1">
                        <Pencil size={12} /> Editar
                      </button>
                      <button type="button" onClick={() => handleToggleActive(u)} className="px-2.5 py-1 rounded-lg border bg-white hover:bg-gray-50 text-xs inline-flex items-center gap-1">
                        <Power size={12} /> {u.active ? 'Desactivar' : 'Activar'}
                      </button>
                      <button type="button" onClick={() => handleDelete(u)} className="px-2.5 py-1 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-xs inline-flex items-center gap-1">
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <UserFormModal
          user={editingUser}
          onClose={closeModal}
          roles={roles}
          rolesLoading={rolesLoading}
          flash={flash}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Pestaña "Roles y permisos": dos columnas.
// ---------------------------------------------------------------------
function RolesTab({ roles, rolesLoading, reloadRoles, flash }) {
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formName, setFormName] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedFlags, setSelectedFlags] = useState({});
  const [saving, setSaving] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const res = await api.admin.roles.get(id);
      const role = res?.data || null;
      setDetail(role);
      const flagsState = {};
      (role?.default_permission_keys || []).forEach((k) => { flagsState[k] = true; });
      setSelectedFlags(flagsState);
    } catch (_) {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  const startCreate = () => {
    setCreating(true);
    setSelectedId(null);
    setDetail(null);
    setFormName('');
    setFormNotes('');
  };

  const saveNewRole = async (duplicateFromId) => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const payload = { display_name: formName.trim(), notes: formNotes.trim() || undefined, is_active: true };
      if (duplicateFromId) payload.duplicate_from_role_id = duplicateFromId;
      const res = await api.admin.roles.upsert(payload);
      flash('success', 'Rol creado');
      setCreating(false);
      await reloadRoles();
      if (res?.id) setSelectedId(res.id);
    } catch (e) {
      flash('error', e?.message || 'No fue posible crear el rol');
    } finally {
      setSaving(false);
    }
  };

  const saveDefaultPermissions = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const permKeys = Object.keys(selectedFlags).filter((k) => selectedFlags[k]);
      await api.admin.roles.setDefaultPermissions({ role_id: selectedId, perm_keys: permKeys });
      flash('success', 'Permisos por defecto actualizados');
      await reloadRoles();
      await loadDetail(selectedId);
    } catch (e) {
      flash('error', e?.message || 'No fue posible guardar los permisos');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (role) => {
    try {
      await api.admin.roles.setActive({ id: role.id, is_active: !role.is_active });
      flash('success', role.is_active ? 'Rol desactivado' : 'Rol activado');
      await reloadRoles();
    } catch (e) {
      flash('error', e?.message || 'No fue posible actualizar el rol');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
      <div className="rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
          <div className="text-sm font-semibold">Roles</div>
          <button type="button" onClick={startCreate} className="text-xs px-2.5 py-1 rounded-lg border bg-white hover:bg-gray-50 inline-flex items-center gap-1">
            <Plus size={12} /> Crear rol
          </button>
        </div>
        <div className="divide-y divide-gray-100 max-h-[560px] overflow-y-auto">
          {rolesLoading && <div className="px-4 py-6 text-sm text-gray-500 text-center">Cargando...</div>}
          {!rolesLoading && roles.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => { setCreating(false); setSelectedId(r.id); }}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${selectedId === r.id ? 'bg-blue-50' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-900 text-sm">{r.display_name}</div>
                {!r.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">Inactivo</span>}
              </div>
              {r.notes && <div className="text-xs text-gray-500 truncate">{r.notes}</div>}
              <div className="text-xs text-gray-400 mt-1">{r.user_count} usuario{r.user_count === 1 ? '' : 's'} · {r.permission_count} permiso{r.permission_count === 1 ? '' : 's'}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 p-5">
        {creating ? (
          <div>
            <div className="text-sm font-semibold text-gray-800 mb-3">Nuevo rol</div>
            <div className="space-y-3 max-w-md">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descripción / notas</label>
                <textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2">
                <button type="button" disabled={saving || !formName.trim()} onClick={() => saveNewRole(null)} className="px-3 py-2 rounded-lg text-white text-sm disabled:opacity-50" style={{ backgroundColor: '#059669' }}>
                  Crear
                </button>
                <button type="button" onClick={() => setCreating(false)} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm">Cancelar</button>
              </div>
            </div>
          </div>
        ) : !selectedId ? (
          <div className="text-sm text-gray-500">Selecciona un rol de la lista, o crea uno nuevo.</div>
        ) : detailLoading ? (
          <div className="text-sm text-gray-500">Cargando...</div>
        ) : detail ? (
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-lg font-semibold text-gray-900">{detail.display_name}</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setFormName(`${detail.display_name} (copia)`); setFormNotes(detail.notes || ''); setCreating('duplicate'); }}
                  className="text-xs px-2.5 py-1 rounded-lg border bg-white hover:bg-gray-50 inline-flex items-center gap-1"
                >
                  <Copy size={12} /> Duplicar
                </button>
                <button type="button" onClick={() => toggleActive(detail)} className="text-xs px-2.5 py-1 rounded-lg border bg-white hover:bg-gray-50 inline-flex items-center gap-1">
                  <Power size={12} /> {detail.is_active ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </div>
            {detail.notes && <div className="text-sm text-gray-500 mb-4">{detail.notes}</div>}
            {creating === 'duplicate' && (
              <div className="mb-4 border rounded-lg p-3 bg-blue-50 border-blue-200 max-w-md">
                <div className="text-sm font-medium mb-2">Duplicar como nuevo rol</div>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mb-2" />
                <div className="flex gap-2">
                  <button type="button" disabled={saving || !formName.trim()} onClick={() => saveNewRole(detail.id)} className="px-3 py-1.5 rounded-lg text-white text-sm disabled:opacity-50" style={{ backgroundColor: '#059669' }}>Crear copia</button>
                  <button type="button" onClick={() => setCreating(false)} className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm">Cancelar</button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-800">Permisos por defecto de este rol</div>
              <button type="button" disabled={saving} onClick={saveDefaultPermissions} className="text-xs px-2.5 py-1 rounded-lg text-white disabled:opacity-50" style={{ backgroundColor: '#2563EB' }}>
                {saving ? 'Guardando...' : 'Guardar permisos'}
              </button>
            </div>
            <div className="space-y-2">
              {PERMISSION_CATEGORIES.map((cat) => (
                <AccordionSection
                  key={cat.id}
                  id={cat.id}
                  title={cat.label}
                  badge={cat.flags.length}
                  accentColor={cat.warning ? '#DC2626' : '#2563EB'}
                  open={openCategory === cat.id}
                  onToggle={(id) => setOpenCategory((v) => (v === id ? null : id))}
                >
                  {cat.warning && (
                    <div className="mb-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{cat.warning}</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    {cat.flags.map((flag) => {
                      const permKey = FLAG_PERM_KEY(flag);
                      return (
                        <label key={flag} className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-gray-50 text-sm">
                          <input
                            type="checkbox"
                            checked={!!selectedFlags[permKey]}
                            onChange={() => setSelectedFlags((f) => ({ ...f, [permKey]: !f[permKey] }))}
                          />
                          {FLAG_LABELS[flag]}
                        </label>
                      );
                    })}
                  </div>
                </AccordionSection>
              ))}
            </div>
            <div className="text-xs text-gray-400 mt-3">
              Estos valores se aplican como plantilla al presionar "Aplicar valores por defecto del rol" en la ficha de un usuario — editar aquí no cambia retroactivamente a quienes ya lo tienen asignado.
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Componente raíz
// ---------------------------------------------------------------------
export default function AdministracionUsuariosYRoles({ location } = {}) {
  const [outerTab, setOuterTab] = useState('usuarios');
  // Reporta a Administración TI > Auditoría qué pestaña de Usuarios y
  // Roles está viendo el usuario (ver X-Senerfi-Page en src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/administracion-ti/usuarios/${outerTab || ''}`);
  }, [location, outerTab]);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState(null);

  const flash = useCallback((type, text) => {
    setStatusMsg({ type, text });
    setTimeout(() => setStatusMsg(null), 2500);
  }, []);

  const reloadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await api.admin.roles.list();
      setRoles(Array.isArray(res?.data) ? res.data : []);
    } catch (_) {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => { reloadRoles(); }, [reloadRoles]);

  const tabs = [
    { id: 'usuarios', label: 'Usuarios', icon: Users },
    { id: 'roles', label: 'Roles y permisos', icon: ShieldCheck },
    { id: 'seguridad', label: 'Seguridad', icon: ShieldCheck },
    { id: 'firmas', label: 'Firmas', icon: PenLine },
    { id: 'auditoria', label: 'Auditoría', icon: History },
  ];

  return (
    <div>
      {statusMsg && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-sm ${statusMsg.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
          {statusMsg.text}
        </div>
      )}
      <Tabs tabs={tabs} value={outerTab} onChange={setOuterTab} className="mb-5" />

      {outerTab === 'usuarios' && <UsersTab roles={roles} rolesLoading={rolesLoading} flash={flash} />}
      {outerTab === 'roles' && <RolesTab roles={roles} rolesLoading={rolesLoading} reloadRoles={reloadRoles} flash={flash} />}
      {outerTab === 'seguridad' && <TotpApprovals />}
      {outerTab === 'firmas' && <FirmasTab flash={flash} />}
      {outerTab === 'auditoria' && <AdministracionAuditTab />}
    </div>
  );
}

// Pestaña "Firmas": lista enfocada en subir/ver la firma en archivo de
// cada colaborador, reutilizando SignatureCell tal cual.
function FirmasTab({ flash }) {
  const { users } = useUserPrefs();
  const signatureByCedula = useSignatureByCedula();
  const [search, setSearch] = useState('');
  const [signatureOverrides, setSignatureOverrides] = useState({});

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users || [];
    return (users || []).filter((u) => String(u.name || '').toLowerCase().includes(q) || String(u.responsibleCedula || '').includes(q));
  }, [users, search]);

  return (
    <div>
      <div className="relative max-w-sm mb-4">
        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o cédula" className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm bg-white" />
      </div>
      <div className="rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Usuario</th>
                <th className="px-4 py-3 font-medium">Cédula</th>
                <th className="px-4 py-3 font-medium">Firma</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const digits = onlyDigits(u.responsibleCedula);
                const signatureUrl = signatureOverrides[digits] || signatureByCedula[digits] || '';
                return (
                  <tr key={u.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                    <td className="px-4 py-3 text-gray-600">{u.responsibleCedula || '-'}</td>
                    <td className="px-4 py-3">
                      <SignatureCell
                        cedula={u.responsibleCedula}
                        signatureUrl={signatureUrl}
                        onUploaded={(uploadedDigits, url) => { setSignatureOverrides((s) => ({ ...s, [uploadedDigits]: url })); flash('success', 'Firma cargada'); }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Pestaña "Auditoría" a nivel de toda la sección — pre-filtrada al módulo
// administracion-ti, distinta de la pantalla general de Auditoría (que no
// se toca en esta fase).
function AdministracionAuditTab() {
  return <UserAuditTab userId={null} moduleKey="administracion-ti" requireUser={false} />;
}
